import moment from "moment";
import CodeService from "@/services/codeService";

export const CodeStore = {
    namespaced: true,
    state: {
        tabs: {
            all: {
                tableData: [],
                totalRecords: 0,
                rowsPerPage: 10,
                currentPage: 1,
                searchText: "",
                fromDate: null,
                toDate: null,
                status: "",
                hasQuery: "",
                chargeable: "",
                sortField: "created_at",
                sortOrder: "desc",
            },
            query_open_not_responded: {
                tableData: [],
                totalRecords: 0,
                rowsPerPage: 10,
                currentPage: 1,
                searchText: "",
                fromDate: null,
                toDate: null,
                status: "UNDER_QUERY",
                hasQuery: true,
                chargeable: "",
                sortField: "created_at",
                sortOrder: "desc",
            },
            chargeable_true: {
                tableData: [],
                totalRecords: 0,
                rowsPerPage: 10,
                currentPage: 1,
                searchText: "",
                fromDate: null,
                toDate: null,
                status: "",
                hasQuery: "",
                chargeable: true,
                sortField: "created_at",
                sortOrder: "desc",
            },
        },
        currentTab: "all",
        isLoading: false,
    },
    mutations: {
        SET_TABLE_DATA(state, { tab, data }) {
            state.tabs[tab].tableData = data.data;
            state.tabs[tab].totalRecords = data.count;
        },
        UPDATE_CURRENT_PAGE(state, { tab, page }) {
            state.tabs[tab].currentPage = page;
        },
        UPDATE_ROWS_PER_PAGE(state, { tab, rows }) {
            state.tabs[tab].rowsPerPage = rows;
        },
        UPDATE_SORT_OPTIONS(state, { tab, field, order }) {
            state.tabs[tab].sortField = field;
            state.tabs[tab].sortOrder = order;
        },
        UPDATE_CURRENT_TAB(state, currentTab) {
            state.currentTab = currentTab;
        },
        SET_LOADING(state, isLoading) {
            state.isLoading = isLoading;
        },
        UPDATE_FROM_DATE(state, { tab, fromDate }) {
            state.tabs[tab].fromDate = fromDate;
        },
        UPDATE_TO_DATE(state, { tab, toDate }) {
            state.tabs[tab].toDate = toDate;
        },
        UPDATE_SEARCH_TEXT(state, { tab, searchText }) {
            state.tabs[tab].searchText = searchText;
        },
    },
    actions: {
        async fetchTableData({ commit, state }) {
            commit("SET_LOADING", true);
            const currentTabState = state.tabs[state.currentTab];
            const params = buildParams(currentTabState);
            const queryString = buildQueryString(params);

            try {
                const data = await CodeService.searchAllCodes(`?${queryString}`);
                commit("SET_TABLE_DATA", { tab: state.currentTab, data });
            } catch (error) {
                commit("SET_TABLE_DATA", { tab: state.currentTab, data: { data: [], count: 0 } });
                console.error("Error fetching data:", error);
            } finally {
                commit("SET_LOADING", false);
            }
        },
        updateTableSettings({ commit, dispatch, state }, { rows, page }) {
            commit("UPDATE_ROWS_PER_PAGE", { tab: state.currentTab, rows });
            commit("UPDATE_CURRENT_PAGE", { tab: state.currentTab, page: page + 1 });
            dispatch("fetchTableData");
        },
        updateSortOptions({ commit, dispatch, state }, { field, order }) {
            commit("UPDATE_SORT_OPTIONS", { tab: state.currentTab, field, order });
            dispatch("fetchTableData");
        },
        updateSearchCriteria({ commit, state }, { searchText, fromDate, toDate }) {
            const tab = state.currentTab;
            commit("UPDATE_SEARCH_TEXT", { tab, searchText });
            commit("UPDATE_FROM_DATE", { tab, fromDate });
            commit("UPDATE_TO_DATE", { tab, toDate });
        },
        updateTab({ commit, dispatch }, currentTab) {
            commit("UPDATE_CURRENT_TAB", currentTab);
            dispatch("fetchTableData");
        },
    },
};

function buildQueryString(params) {
    return Object.entries(params)
        .filter(([_, value]) => value !== undefined && value !== null && value !== "")
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join("&");
}

function buildParams(currentTabState) {
    let params = {
        page: currentTabState.currentPage,
        limit: currentTabState.rowsPerPage,
        orderBy: currentTabState.sortField,
        order: currentTabState.sortOrder,
    };

    if (currentTabState.searchText) {
        params.searchTerm = currentTabState.searchText;
    }

    const formattedFromDate = formatDateToUKFormat(currentTabState.fromDate);
    if (formattedFromDate && formattedFromDate !== "Invalid date") {
        params.fromDate = formattedFromDate;
    }

    const formattedToDate = formatDateToUKFormat(currentTabState.toDate);
    if (formattedToDate && formattedToDate !== "Invalid date") {
        params.toDate = formattedToDate;
    }

    if (currentTabState.status) {
        params.status = currentTabState.status;
    }

    if (currentTabState.hasQuery !== "" && currentTabState.hasQuery !== null && currentTabState.hasQuery !== undefined) {
        params.hasQuery = currentTabState.hasQuery;
    }

    if (currentTabState.chargeable !== "" && currentTabState.chargeable !== null && currentTabState.chargeable !== undefined) {
        params.chargeable = currentTabState.chargeable;
    }

    return params;
}

function formatDateToUKFormat(dateInput) {
    if (!dateInput) return null;
    const date = typeof dateInput === "string" ? moment(dateInput) : moment(dateInput);
    return date.format("DD/MM/YYYY");
}
